import { useState, useRef, useMemo, useEffect } from "react";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Card from "react-bootstrap/Card";
import Form from "react-bootstrap/Form";
import Breadcrumb from "react-bootstrap/Breadcrumb";
import { BsGear } from "react-icons/bs";
import { Link } from "react-router-dom";
import { useQuery, NetworkStatus } from "@apollo/client";
import { Alert, Button, LoadingSpinner } from "@evos/components";
import { useWindowSize } from "@evos/hooks";
import type ReactEChartsCore from "echarts-for-react";

import Header from "components/Header";

import { USER_SITES, CHARGER_DETAILS, CHARGER_LOADS, SITE_PHASE_CURRENT } from "./gql/gql";
import getDevicesOnSite from "./functions/getDevicesOnSite";
import calculateLiveSitePhaseCurrentData from "./functions/calculateLiveSitePhaseCurrentData";
import { getChargerStatusString } from "./lib/getChargerStatusString";
import EnergyPriorities from "./components/EnergyPriorities";
import SiteTree from "./components/SiteTree";
import ConfigureDeviceForm from "./components/ConfigureDeviceForm";
import ChargerLoadChart from "./components/ChargerLoadChart/ChargerLoadChart";
import SitePhaseCurrentLiveUsageChart from "./components/SitePhaseCurrentLiveUsageChart";
import {
  useSetVoltageOutputMutation,
  useSetSessionLimitMutation,
  useSetManagementStrategyMutation,
  useSetSolarStrategyMutation,
} from "./hooks/useSiteManagerMutations";

import type {
  UserSitesConnection,
  ParsedSiteFeed,
  ParsedPowerInputTimeseriesData,
  SetVoltageOutputArguments,
  SetSessionLimitArguments,
} from "./types";

export default function SiteManager() {
  const windowWidth = useWindowSize().width ?? window.innerWidth;
  const isSmallToMediumScreen = windowWidth < 992;

  const siteTreeRef = useRef<ReactEChartsCore>(null);
  const siteTreeCardContainerRef = useRef<HTMLDivElement>(null);
  const configureDeviceFormContainerRef = useRef<HTMLDivElement>(null);

  const [selectedSite, setSelectedSite] = useState<string | null>(null);
  const [showConfigureMenu, setShowConfigureMenu] = useState(false);
  const [selectedNodeToConfigure, setSelectedNodeToConfigure] = useState<{ type: string; name: string } | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const { data: userSitesData, error: userSitesError, networkStatus: userSitesNetworkStatus, refetch: refetchUserSites } = useQuery<
    UserSitesConnection<string>
  >(USER_SITES, {
    variables: { n: 100 },
    notifyOnNetworkStatusChange: true,
    errorPolicy: "all",
    onCompleted: (data) => {
      if (data?.sites?.edges?.length > 0) {
        setSelectedSite(data.sites.edges[0].node.ref);
      } else {
        setSelectedSite("none");
      }
    },
  });

  const parsedSiteEnergyManagementData = useMemo((): UserSitesConnection<ParsedSiteFeed[]>["sites"] | undefined => {
    const sites = userSitesData?.sites;
    const edges = sites?.edges;
    if (edges == null || edges.length === 0 || sites == null) return undefined;
    return {
      ...sites,
      edges: edges.map((edge) => ({
        ...edge,
        node: {
          ...edge.node,
          feeds: typeof edge.node.feeds === "string" ? (JSON.parse(edge.node.feeds as string) as ParsedSiteFeed[]) : (edge.node.feeds as ParsedSiteFeed[]),
        },
      })),
    };
  }, [userSitesData]);

  const selectedSiteNode = useMemo(() => {
    if (selectedSite == null || selectedSite === "none" || selectedSite === "loading" || selectedSite === "error") return undefined;
    return parsedSiteEnergyManagementData?.edges?.find((e) => e.node.ref === selectedSite)?.node;
  }, [selectedSite, parsedSiteEnergyManagementData]);

  const currentSiteDataForDisplay = useMemo(() => {
    if (selectedSiteNode == null || parsedSiteEnergyManagementData == null) return undefined;
    const edge = parsedSiteEnergyManagementData.edges?.find((e) => e.node.ref === selectedSite);
    if (edge == null) return undefined;
    return { ...parsedSiteEnergyManagementData, edges: [edge] } as UserSitesConnection<ParsedSiteFeed[]>["sites"];
  }, [selectedSiteNode, parsedSiteEnergyManagementData, selectedSite]);

  const parsedSiteFeeds = selectedSiteNode?.feeds;
  const devicesOnSite = useMemo(() => getDevicesOnSite(parsedSiteFeeds), [parsedSiteFeeds]);
  const chargersOnSite = selectedSiteNode?.serials ?? [];
  const siteId = selectedSiteNode?.id ?? "";
  const siteRef = selectedSite == null || selectedSite === "none" || selectedSite === "loading" || selectedSite === "error" ? "" : selectedSite;
  const siteLimit = selectedSiteNode?.site_limit ?? 0;

  const { data: sitePhaseCurrentData } = useQuery<{
    HistoricalDataContainer?: { power_input_data?: Array<{ input_id?: string; data?: string }> };
  }>(SITE_PHASE_CURRENT, {
    variables: {
      input_ids: parsedSiteFeeds?.[0]?.id != null ? [String(parsedSiteFeeds[0].id)] : [],
      site_ref: siteRef,
    },
    errorPolicy: "all",
    skip: siteRef === "" || parsedSiteFeeds?.[0]?.id == null,
    pollInterval: 60000,
  });

  const parsedSiteLiveData = useMemo((): ParsedPowerInputTimeseriesData | undefined => {
    const raw = sitePhaseCurrentData?.HistoricalDataContainer?.power_input_data?.[0]?.data;
    if (raw == null) return undefined;
    try {
      return JSON.parse(raw) as ParsedPowerInputTimeseriesData;
    } catch {
      return undefined;
    }
  }, [sitePhaseCurrentData]);

  const sitePhaseCurrentGraphData = useMemo(
    () => calculateLiveSitePhaseCurrentData(parsedSiteLiveData),
    [parsedSiteLiveData]
  );

  const phaseChartMaxCapacity = siteLimit > 0 ? siteLimit : 100;

  const { data: chargerDetailsData, refetch: refetchChargerDetails } = useQuery<{
    chargers?: { edges?: Array<{ node?: { serial?: string; details?: Record<string, unknown>; salesforce_details?: Record<string, unknown> } }> };
  }>(CHARGER_DETAILS, {
    variables: { n: 30, where: { serial: { in: chargersOnSite } } },
    errorPolicy: "all",
    skip: chargersOnSite.length === 0,
  });

  const { data: chargerLoadsData, loading: chargerLoadsLoading } = useQuery<{
    chargers?: { edges?: Array<{
      node?: { serial?: string; transactions?: { edges?: Array<{
        node?: { meterStop?: number | null; meterValues?: Array<{ CurrentImportL1?: number; CurrentImportL2?: number; CurrentImportL3?: number; CurrentOffered?: number }> };
      }> } };
    }> };
  }>(CHARGER_LOADS, {
    variables: { chargers_n: 30, chargers_where: { serial: { in: chargersOnSite } }, transactions_n: 1 },
    errorPolicy: "all",
    skip: chargersOnSite.length === 0,
  });

  const chargerDetails = useMemo(() => {
    const edges = chargerDetailsData?.chargers?.edges;
    const loadEdges = chargerLoadsData?.chargers?.edges;
    if (edges == null) return undefined;
    return edges.map((e: { node?: { serial?: string; details?: Record<string, unknown>; salesforce_details?: Record<string, unknown> } }) => {
      const details = e?.node?.details ?? e?.node?.salesforce_details;
      const serial = e?.node?.serial ?? "";
      const txEdge = loadEdges?.find((l) => l?.node?.serial === serial)?.node?.transactions?.edges?.[0];
      const isCharging = txEdge != null && (txEdge.node as { meterStop?: number | null })?.meterStop == null;
      return {
        serial,
        name: details?.name as string | undefined,
        session_limit: details?.session_limit as number | undefined,
        circuit_amp_limit: details?.circuit_amp_limit as number | undefined,
        last_comms: details?.last_comms as string | null | undefined,
        manufacturer: details?.manufacturer as string | undefined,
        model: details?.model as string | undefined,
        isCharging,
        chargerStatus: getChargerStatusString(
          details?.ocpp_firmware_status as string | undefined,
          details?.evos_custom_status as string | undefined,
          details?.ocpp_status as string | undefined
        ),
      };
    });
  }, [chargerDetailsData, chargerLoadsData]);

  const chargerOnlineStatus = useMemo(
    () => chargerDetails?.map((c) => ({ serial: c.serial, last_comms: (c.last_comms ?? "") as string })),
    [chargerDetails]
  );

  const [setVoltageOutput, setVoltageOutputLoading, setVoltageOutputError] = useSetVoltageOutputMutation();
  const [setSessionLimit, sessionLimitLoading, sessionLimitError] = useSetSessionLimitMutation();
  const [setManagementStrategy, setManagementStrategyLoading, setManagementStrategyError] = useSetManagementStrategyMutation();
  const [setSolarStrategy, setSolarStrategyLoading, setSolarStrategyError] = useSetSolarStrategyMutation();

  const onSuccess = () => {
    setSubmitSuccess(true);
    refetchUserSites();
    refetchChargerDetails();
  };

  const onInputStrategyChange = async (strategy: "solar_charging" | "zero_export" | "no_strategy") => {
    await setSolarStrategy({ site_ref: siteRef, strategy }, onSuccess);
  };

  const onOutputStrategyChange = async (strategy: "soc" | "static" | "dynamic") => {
    await setManagementStrategy({ site_ref: siteRef, device_id: siteId, strategy }, onSuccess);
  };

  const onDistBoardSubmit = async (data: SetVoltageOutputArguments) => {
    await setVoltageOutput(
      { device_id: data.device_id, site_ref: data.site_ref, limit: Number(data.limit) },
      onSuccess
    );
  };

  const onChargerSubmit = async (data: SetSessionLimitArguments) => {
    await setSessionLimit({ serial: data.serial, charge: data.charge }, onSuccess);
  };

  const setSelectedNodeToConfigureHandler = (node: { type: string; name: string } | null) => {
    setSelectedNodeToConfigure(node);
    if (submitSuccess) setSubmitSuccess(false);
  };

  const getConfigureMenuHeading = (nodeType: string | undefined) => {
    switch (nodeType) {
    case "charger":
      return "Configure charger";
    case "mainDistBoard":
    case "subDistBoard":
      return "Configure dist board";
    case "circuit":
      return "Circuit";
    default:
      return "Configure site";
    }
  };

  useEffect(() => {
    const chart = siteTreeRef.current?.getEchartsInstance();
    const series = (chart?.getOption() as { series?: Array<{ selectedMode?: boolean }> })?.series?.[0];
    const isSelectedMode = series?.selectedMode === true;

    if (showConfigureMenu && isSelectedMode && selectedNodeToConfigure == null) {
      chart?.dispatchAction({
        type: "select",
        seriesIndex: 0,
        name: devicesOnSite?.[0]?.label ?? "",
      });
      setSelectedNodeToConfigure({ type: "mainDistBoard", name: devicesOnSite?.[0]?.label ?? "" });
    } else if (!showConfigureMenu && !isSelectedMode && selectedNodeToConfigure != null) {
      chart?.dispatchAction({
        type: "unselect",
        seriesIndex: 0,
        name: selectedNodeToConfigure.name,
      });
      setSelectedNodeToConfigure(null);
    }
  }, [devicesOnSite, showConfigureMenu, selectedNodeToConfigure]);

  const userSitesInitialLoading = userSitesNetworkStatus === NetworkStatus.loading;
  const sites = userSitesData?.sites;
  const hasSites = (sites?.edges?.length ?? 0) > 0;

  return (
    <div id="SiteManager">
      <Header />
      <Container>
        <div className="d-flex flex-wrap justify-content-between align-items-center row-gap-3 gap-3 py-3">
          <Breadcrumb>
            <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/site-manager" }} active>
              Site Manager
            </Breadcrumb.Item>
          </Breadcrumb>
          <Form.Select
            className="p-2"
            value={userSitesInitialLoading || selectedSite == null ? "loading" : selectedSite}
            onChange={(e) => setSelectedSite(e.target.value)}
            style={{ minWidth: "200px" }}
            disabled={userSitesInitialLoading}
          >
            {userSitesInitialLoading || selectedSite == null ? (
              <option value="loading">Loading...</option>
            ) : userSitesError != null ? (
              <option value="error">Error</option>
            ) : (
              sites?.edges?.map((site) => (
                <option key={site.node.id} value={site.node.ref}>
                  {site.node.name}
                </option>
              ))
            )}
          </Form.Select>
        </div>

        <Row>
          <Col xs={12}>
            <EnergyPriorities
              optimisationStrategy={selectedSiteNode?.optimisationStrategy}
              solarChargingStrategy={selectedSiteNode?.solarStrategy}
              prioritiesLoading={userSitesInitialLoading}
              onInputPriorityChange={onInputStrategyChange}
              onOutputPriorityChange={onOutputStrategyChange}
              inputPriorityChangeLoading={setSolarStrategyLoading}
              outputPriorityChangeLoading={setManagementStrategyLoading}
              inputPriorityChangeError={setSolarStrategyError}
              outputPriorityChangeError={setManagementStrategyError}
            />
          </Col>
        </Row>

        {hasSites && siteRef !== "" && (
          <Row className="mb-4">
            <Col xs={12}>
              <Card className="p-4">
                <h3 className="fw-medium mb-3">Energy management overview</h3>
                {sitePhaseCurrentGraphData != null && sitePhaseCurrentGraphData.length > 0 && (
                  <>
                    <h4 className="fw-medium pt-2 pb-2">Live data (phase current, last 12 hours)</h4>
                    <SitePhaseCurrentLiveUsageChart
                      sitePhaseCurrentData={sitePhaseCurrentGraphData}
                      maxCapacity={phaseChartMaxCapacity}
                      isSmallToMediumScreen={isSmallToMediumScreen}
                    />
                  </>
                )}
                {chargersOnSite.length > 0 && (
                  <>
                    <h4 className="fw-medium pt-4 pb-2">Load by charger</h4>
                    <div className="d-flex justify-content-start flex-wrap gap-4 pt-2">
                      {chargersOnSite.map((serial) => {
                        const loadEdge = chargerLoadsData?.chargers?.edges?.find((c) => c?.node?.serial === serial)?.node?.transactions?.edges?.[0]?.node;
                        const lastMeter = loadEdge?.meterValues?.at(-1);
                        const isCharging = loadEdge?.meterStop == null;
                        const currentUsed = isCharging && lastMeter != null
                          ? Math.max(
                            lastMeter.CurrentImportL1 ?? 0,
                            lastMeter.CurrentImportL2 ?? 0,
                            lastMeter.CurrentImportL3 ?? 0
                          )
                          : 0;
                        const currentOffered = lastMeter?.CurrentOffered ?? 0;
                        const detail = chargerDetails?.find((c) => c.serial === serial);
                        const maxCapacity = detail?.session_limit ?? detail?.circuit_amp_limit ?? 32;
                        return (
                          <div key={serial} className="d-flex justify-content-center mb-3">
                            <ChargerLoadChart
                              currentUsed={currentUsed}
                              currentOffered={currentOffered}
                              maxCapacity={maxCapacity}
                              isLoading={chargerLoadsLoading}
                              colours={["#001dd2", "#7bd1a3", "#c2c9f4"]}
                              title={detail?.name ?? serial}
                              chargerStatus={detail?.chargerStatus}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </Card>
            </Col>
          </Row>
        )}

        <Row className="mb-4">
          <h3 className="fw-medium my-3">Site tree</h3>
          <Col xs={12} md={showConfigureMenu ? 8 : 12}>
            {!userSitesInitialLoading && parsedSiteEnergyManagementData == null && !hasSites ? (
              <div className="d-flex">
                <Alert variant="info" className="mb-0 py-4 w-100" showIcon message="Site energy management is not configured." />
              </div>
            ) : userSitesError == null ? (
              <Card className="p-4 mb-3" style={{ overflow: "auto", scrollMarginTop: "80px" }} ref={siteTreeCardContainerRef}>
                <div style={{ position: "sticky", left: 0, width: "100%" }}>
                  <Row>
                    <Col xs={12} className="d-flex justify-content-start justify-content-lg-end pt-3 pt-lg-0">
                      <Button
                        variant="outline-secondary"
                        disabled={userSitesInitialLoading || chargerDetails == null}
                        onClick={() => {
                          if (showConfigureMenu) {
                            setShowConfigureMenu(false);
                            if (submitSuccess) setSubmitSuccess(false);
                          } else {
                            setShowConfigureMenu(true);
                            if (isSmallToMediumScreen) {
                              setTimeout(() => configureDeviceFormContainerRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
                            }
                          }
                        }}
                      >
                        Configure <BsGear />
                      </Button>
                    </Col>
                  </Row>
                </div>
                {!userSitesInitialLoading && devicesOnSite.length > 0 && chargerDetails != null ? (
                  <SiteTree
                    siteData={currentSiteDataForDisplay}
                    chargerOnlineStatus={chargerOnlineStatus}
                    chargerDetails={chargerDetails}
                    siteTreeRef={siteTreeRef}
                    siteTreeCardContainerRef={siteTreeCardContainerRef}
                    isSmallToMediumScreen={isSmallToMediumScreen}
                    isConfigureMenuOpen={showConfigureMenu}
                    selectedNodeToConfigure={selectedNodeToConfigure}
                    setSelectedNodeToConfigure={setSelectedNodeToConfigureHandler}
                  />
                ) : (
                  <div className="d-flex align-items-center justify-content-center py-5">
                    <LoadingSpinner />
                  </div>
                )}
              </Card>
            ) : (
              <Alert variant="danger" className="mb-4 mt-3" showIcon message="Failed to load site data." />
            )}
          </Col>

          {showConfigureMenu && (
            <Col xs={12} md={4}>
              <Card className="px-3 pt-2 mt-4 mt-md-0" ref={configureDeviceFormContainerRef}>
                <h4 className="fw-medium my-3 py-2">
                  {getConfigureMenuHeading(selectedNodeToConfigure?.type)} - {selectedNodeToConfigure?.name}
                </h4>
                {selectedNodeToConfigure?.type === "charger" ? (
                  <>
                    <ConfigureDeviceForm
                      key={selectedNodeToConfigure.name}
                      formDefaultValues={{
                        serial: selectedNodeToConfigure.name ?? "",
                        charge: chargerDetails?.find((c) => c.serial === selectedNodeToConfigure?.name)?.session_limit ?? 32,
                        circuit_amp_limit: chargerDetails?.find((c) => c.serial === selectedNodeToConfigure?.name)?.circuit_amp_limit ?? 32,
                      }}
                      deviceType="charger"
                      submitLoading={sessionLimitLoading}
                      onSubmit={(data) => onChargerSubmit(data as SetSessionLimitArguments)}
                      closeForm={() => {
                        if (isSmallToMediumScreen) {
                          siteTreeCardContainerRef.current?.scrollIntoView({ behavior: "smooth" });
                          setTimeout(() => setShowConfigureMenu(false), 500);
                        } else {
                          setShowConfigureMenu(false);
                        }
                        if (submitSuccess) setSubmitSuccess(false);
                      }}
                    />
                    {sessionLimitError != null && (
                      <Alert variant="danger" showIcon dismissible className="pt-3" message="Failed to set session limit." />
                    )}
                    {sessionLimitError == null && !sessionLimitLoading && submitSuccess && (
                      <Alert variant="success" showIcon className="pt-3" message="Charger updated successfully." />
                    )}
                  </>
                ) : selectedNodeToConfigure?.type === "mainDistBoard" || selectedNodeToConfigure?.type === "subDistBoard" ? (
                  <>
                    <ConfigureDeviceForm
                      key={selectedNodeToConfigure.name}
                      formDefaultValues={{
                        site_ref: siteRef,
                        device_id:
                          devicesOnSite?.[0]?.subDistBoards.find((s) => s.label === selectedNodeToConfigure?.name) != null
                            ? devicesOnSite?.[0]?.subDistBoards.find((s) => s.label === selectedNodeToConfigure?.name)?.id ?? ""
                            : devicesOnSite?.[0]?.id ?? "",
                        limit:
                          devicesOnSite?.[0]?.subDistBoards.find((s) => s.label === selectedNodeToConfigure?.name) != null
                            ? devicesOnSite?.[0]?.subDistBoards.find((s) => s.label === selectedNodeToConfigure?.name)?.maxOutput ?? 0
                            : devicesOnSite?.[0]?.maxOutput ?? 0,
                      }}
                      deviceType="distBoard"
                      submitLoading={setVoltageOutputLoading}
                      onSubmit={(data) => onDistBoardSubmit(data as SetVoltageOutputArguments)}
                      closeForm={() => {
                        if (isSmallToMediumScreen) {
                          siteTreeCardContainerRef.current?.scrollIntoView({ behavior: "smooth" });
                          setTimeout(() => setShowConfigureMenu(false), 500);
                        } else {
                          setShowConfigureMenu(false);
                        }
                        if (submitSuccess) setSubmitSuccess(false);
                      }}
                    />
                    {setVoltageOutputError != null && (
                      <Alert variant="danger" showIcon dismissible className="pt-3" message="Failed to set voltage output." />
                    )}
                    {setVoltageOutputError == null && !setVoltageOutputLoading && submitSuccess && (
                      <Alert variant="success" showIcon className="pt-3" message="Charger updated successfully." />
                    )}
                  </>
                ) : null}
              </Card>
            </Col>
          )}
        </Row>
      </Container>
    </div>
  );
}
